import {
  AusweisTypEnum,
  CdsScrypt,
  MIGRATED_GEBURTSORT_PLACEHOLDER,
  MIGRATED_HEIMATORT_PLACEHOLDER,
  SexEnum,
  StammDaten,
  StammdatenQuestionnaireQuestionKey,
} from '@web/common';

type CannavigiaData = {
  ignoreHashCheck: boolean;
  hash: string;
  idType: 'SP' | 'SI' | 'RP';
  idNumber: string;
  placeOfBirthOrResidence: string;
  dispensaries: string[];
};

export function getCannavigiaSourceData(stammdaten: StammDaten): {
  vorname: string;
  nachname: string;
  sex: SexEnum;
  birthdate: string;
  formattedBirthdate: string;
  idNumber: string;
  isSwiss: boolean;
  birthPlace: string;
  hasRP: boolean;
  idType: 'SP' | 'SI' | 'RP';
  idTypeReadable: string;
} {
  const {
    AusweisNr,
    AusweisType,
    SwissNationality,
    HeimatOrt,
    GeburtsOrt,
    Sex,
    BirthDate,
    Name,
    Surname,
  } = StammdatenQuestionnaireQuestionKey;

  const vorname = stammdaten[Name] ?? 'Anonym';
  const nachname = stammdaten[Surname] ?? 'Anonym';
  const sex = stammdaten[Sex];
  const birthdate = stammdaten[BirthDate];
  const formattedBirthdate = birthdate.replaceAll('-', '.');
  const idNumber = stammdaten[AusweisNr] ?? 'Anonym';
  const isSwiss = stammdaten[SwissNationality];
  const birthPlaceKey = isSwiss ? HeimatOrt : GeburtsOrt;
  const birthPlace = stammdaten[birthPlaceKey];
  // Cannavigia has always required this field. A missing value, or the
  // checklist-migration placeholder for it, means the participant's data is
  // not actually complete - sending either would put wrong data on file.
  if (
    !birthPlace ||
    birthPlace === MIGRATED_HEIMATORT_PLACEHOLDER ||
    birthPlace === MIGRATED_GEBURTSORT_PLACEHOLDER
  ) {
    throw new Error(
      `Cannot determine ${birthPlaceKey} for Cannavigia: value is missing or still a migration placeholder`,
    );
  }
  const hasRP = stammdaten[AusweisType] === AusweisTypEnum.Reisepass;
  const idType = isSwiss ? (hasRP ? 'SP' : 'SI') : 'RP';
  const idTypeReadable = isSwiss
    ? hasRP
      ? 'Schweizer Pass'
      : 'Schweizer Id'
    : 'Reisepass';

  return {
    vorname,
    nachname,
    sex,
    birthdate,
    formattedBirthdate,
    idNumber,
    hasRP,
    isSwiss,
    birthPlace,
    idType,
    idTypeReadable,
  };
}

export async function getCannavigiaData(
  stammdaten: StammDaten | null,
  dispensaries: string,
  ignoreHash = false,
): Promise<CannavigiaData | undefined> {
  if (!stammdaten) return;

  let sourceData;
  try {
    sourceData = getCannavigiaSourceData(stammdaten);
  } catch {
    // missing or still-a-placeholder birthplace: same "cannot build the
    // Cannavigia payload" outcome the caller already handles for !stammdaten
    return;
  }
  const {
    vorname,
    nachname,
    sex,
    formattedBirthdate,
    idNumber,
    birthPlace,
    idType,
  } = sourceData;

  const password = CdsScrypt.generatePassword(
    vorname,
    nachname,
    sex[0].toLocaleUpperCase(),
    formattedBirthdate,
  );

  const hash = await CdsScrypt.calculateHash(password);

  return {
    ignoreHashCheck: ignoreHash,
    hash,
    idType,
    idNumber,
    placeOfBirthOrResidence: birthPlace,
    dispensaries: [dispensaries],
  };
}
